import { map } from 'rxjs';
import type { FundingView, FundingInput } from '../../../core/interfaces/backend/financiaciones_proyectos/financiaciones_proyectos.workflow.interface';
import type { SuccessEnvelope } from '../../../core/interfaces/http/response.interface';
import type { Envelope, Deleted } from '../../../core/interfaces/http/response.interface';
import type { SideData } from '../../../core/interfaces/http/pagination.interface';
import type { FinanciacionesProyectosRead, FinanciacionesProyectosCreate, FinanciacionesProyectosReplace, FinanciacionesProyectosPatch, FinanciacionesProyectosIdentifier, FinanciacionesProyectosDeleteAll } from '../../../core/interfaces/backend/financiaciones_proyectos/financiaciones_proyectos.interface';

import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class FinanciacionesProyectos {
  private readonly http = inject(HttpClient);
  private readonly url = environment.backendUrl.replace(/\/$/, '') + '/pvt/gb/financiaciones_proyectos';
  sideQuery(page = 1, row = 20, sort: keyof FinanciacionesProyectosRead = 'id_registro', direction: 'asc' | 'desc' = 'asc',
    filters: Partial<FinanciacionesProyectosRead> = {}, headers?: HttpHeaders): Observable<HttpResponse<Envelope<SideData<FinanciacionesProyectosRead>>>> {
    return this.http.get<Envelope<SideData<FinanciacionesProyectosRead>>>(this.url + '/side/query', {
      observe: 'response', headers,
      params: { page, row, sort, direction, filters: JSON.stringify(filters) },
    });
  }
  selectAll(headers?: HttpHeaders): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead[]>>> {
    return this.http.get<Envelope<FinanciacionesProyectosRead[]>>(this.url + '/select/all', {
      observe: 'response',
      headers,
    });
  }
  selectReg(
    id_registro: string,
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead>>> {
    return this.http.get<Envelope<FinanciacionesProyectosRead>>(this.url + '/select/reg', {
      observe: 'response',
      headers,
      params: { id_registro },
    });
  }
  selectPgt(
    sheet = 1,
    row = 20,
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead[]>>> {
    return this.http.get<Envelope<FinanciacionesProyectosRead[]>>(this.url + '/select/pgt', {
      observe: 'response',
      headers,
      params: { sheet, row },
    });
  }
  insertRegister(
    body: FinanciacionesProyectosCreate,
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead>>> {
    return this.http.post<Envelope<FinanciacionesProyectosRead>>(this.url + '/insert/register', body, {
      observe: 'response',
      headers,
    });
  }
  insertMultiple(
    body: FinanciacionesProyectosCreate[],
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead[]>>> {
    return this.http.post<Envelope<FinanciacionesProyectosRead[]>>(this.url + '/insert/multiple', body, {
      observe: 'response',
      headers,
    });
  }
  replaceRegister(
    body: FinanciacionesProyectosReplace,
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead>>> {
    return this.http.put<Envelope<FinanciacionesProyectosRead>>(this.url + '/update/register', body, {
      observe: 'response',
      headers,
    });
  }
  replaceMultiple(
    body: FinanciacionesProyectosReplace[],
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead[]>>> {
    return this.http.put<Envelope<FinanciacionesProyectosRead[]>>(this.url + '/update/multiple', body, {
      observe: 'response',
      headers,
    });
  }
  patchRegister(
    body: FinanciacionesProyectosPatch,
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead>>> {
    return this.http.patch<Envelope<FinanciacionesProyectosRead>>(this.url + '/update/register', body, {
      observe: 'response',
      headers,
    });
  }
  patchMultiple(
    body: FinanciacionesProyectosPatch[],
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<FinanciacionesProyectosRead[]>>> {
    return this.http.patch<Envelope<FinanciacionesProyectosRead[]>>(this.url + '/update/multiple', body, {
      observe: 'response',
      headers,
    });
  }
  deleteRegister(
    body: FinanciacionesProyectosIdentifier,
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<Deleted>>> {
    return this.http.delete<Envelope<Deleted>>(this.url + '/delete/register', {
      observe: 'response',
      headers,
      body,
    });
  }
  deleteMultiple(
    body: FinanciacionesProyectosIdentifier[],
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<Deleted>>> {
    return this.http.delete<Envelope<Deleted>>(this.url + '/delete/multiple', {
      observe: 'response',
      headers,
      body,
    });
  }
  deleteAll(
    body: FinanciacionesProyectosDeleteAll,
    headers?: HttpHeaders,
  ): Observable<HttpResponse<Envelope<Deleted>>> {
    return this.http.delete<Envelope<Deleted>>(this.url + '/delete/all', {
      observe: 'response',
      headers,
      body,
    });
  }

  viewProject(projectId:string) {
    return this.http.get<SuccessEnvelope<FundingView>>(this.url + '/project/' + projectId).pipe(map(r => r.data));
  }
  saveProject(projectId:string, body:FundingInput) {
    return this.http.post<SuccessEnvelope<FundingView>>(this.url + '/project/' + projectId, body).pipe(map(r => r.data));
  }
  downloadProjectFile(projectId:string, fileId:string) {
    return this.http.get(this.url + '/project/' + projectId + '/files/' + fileId, {responseType:'blob'});
  }
}
